/**
 * Enhanced Responsive Hook for Parent Portal Refactor
 * 
 * CREATED FOR: Parent Portal refactor & redesign (Phase 1.1)
 * PURPOSE: Replace inconsistent mobile detection with unified responsive system
 * TECH DEBT: Eliminates mixed responsive detection methods throughout Parent Portal
 * 
 * This file addresses critical tech debt in the Parent Portal where:
 * - useIsMobile hook used 768px breakpoint inconsistently
 * - Components used direct window.innerWidth checks
 * - Mobile/desktop detection wasn't aligned with Tailwind breakpoints
 * - Different components had different responsive logic
 * 
 * The new system provides:
 * - Tailwind-aligned breakpoints (sm:640, md:768, lg:1024, xl:1280)
 * - Semantic device type detection (isMobile, isTablet, isDesktop)
 * - Utility functions for responsive logic
 * - Backward compatibility with existing useIsMobile usage
 */

import { useState, useEffect } from 'react';

// Tailwind CSS breakpoints - aligned with default Tailwind config
export const BREAKPOINTS = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;
export type ResponsiveState = {
  width: number;
  height: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isXl: boolean;
  current: Breakpoint | 'xs';
  isAtLeast: (breakpoint: Breakpoint) => boolean;
  isBelow: (breakpoint: Breakpoint) => boolean;
};

/**
 * Enhanced responsive hook that provides comprehensive screen size information
 * aligned with Tailwind CSS breakpoints and modern responsive design practices.
 * 
 * Replaces the simple useIsMobile hook with a more robust system that:
 * - Uses CSS-first responsive detection
 * - Aligns with Tailwind breakpoints exactly
 * - Provides semantic device type detection
 * - Offers utility functions for responsive logic
 * 
 * @returns ResponsiveState object with comprehensive responsive information
 */
export const useResponsive = (): ResponsiveState => {
  const [dimensions, setDimensions] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
    height: typeof window !== 'undefined' ? window.innerHeight : 0
  });

  useEffect(() => {
    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    // Use throttled resize for better performance
    let timeoutId: NodeJS.Timeout;
    const throttledResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(handleResize, 16); // ~60fps
    };

    window.addEventListener('resize', throttledResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', throttledResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  const { width, height } = dimensions;

  // Determine current breakpoint
  const getCurrentBreakpoint = (): Breakpoint | 'xs' => {
    if (width >= BREAKPOINTS['2xl']) return '2xl';
    if (width >= BREAKPOINTS.xl) return 'xl';
    if (width >= BREAKPOINTS.lg) return 'lg';
    if (width >= BREAKPOINTS.md) return 'md';
    if (width >= BREAKPOINTS.sm) return 'sm';
    return 'xs';
  };

  const current = getCurrentBreakpoint();

  // Semantic device type detection
  const isMobile = width < BREAKPOINTS.md; // < 768px
  const isTablet = width >= BREAKPOINTS.md && width < BREAKPOINTS.lg; // 768px - 1023px
  const isDesktop = width >= BREAKPOINTS.lg; // >= 1024px
  const isXl = width >= BREAKPOINTS.xl; // >= 1280px

  // Utility functions for responsive logic
  const isAtLeast = (breakpoint: Breakpoint): boolean => {
    return width >= BREAKPOINTS[breakpoint];
  };

  const isBelow = (breakpoint: Breakpoint): boolean => {
    return width < BREAKPOINTS[breakpoint];
  };

  return {
    width,
    height,
    isMobile,
    isTablet,
    isDesktop,
    isXl,
    current,
    isAtLeast,
    isBelow
  };
};

/**
 * Simple hook for basic mobile detection - maintains backward compatibility
 * with existing useIsMobile usage while using the new responsive system.
 * 
 * MIGRATION NOTE: This hook now uses the enhanced responsive system internally
 * but maintains the same API for backward compatibility during the refactor.
 * 
 * @returns boolean indicating if the current viewport is mobile-sized
 */
export const useIsMobile = (): boolean => {
  const { isMobile } = useResponsive();
  return isMobile;
};

export default useResponsive;